import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { APP_FONT_FAMILY } from "../../constants/fonts";

type SetupCoachmarkProps = {
  open: boolean;
  title: string;
  subtitle: string;
  onDismiss: () => void;
  onNext?: () => void;
  nextLabel?: string;
  fullWidth?: boolean;
  /** Override tooltip placement (defaults depend on variant / fullWidth). */
  placement?: "top" | "bottom" | "left" | "right";
  /** Merged onto the spotlight wrapper (e.g. absolute panel anchors). */
  targetSx?: object;
  /**
   * `menu` — no screen dim (safe inside MuiMenu).
   * `sidebar` | `default` — full-screen portal dim with a lit cutout on the target.
   */
  variant?: "default" | "menu" | "sidebar";
  children: ReactElement;
};

type Hole = { top: number; left: number; width: number; height: number };

function ScreenDimOverlay({
  hole,
  dim,
  zIndex,
}: {
  hole: Hole;
  dim: string;
  zIndex: number;
}) {
  return createPortal(
    <>
      {/* Dim everywhere except the hole (transparent box + huge shadow) */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: hole.left,
          width: hole.width,
          height: hole.height,
          borderRadius: "14px",
          boxShadow: `0 0 0 9999px ${dim}`,
          zIndex,
          pointerEvents: "none",
        }}
      />
      {/* Block clicks on dimmed regions */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(0, hole.top),
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top + hole.height,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: 0,
          width: Math.max(0, hole.left),
          height: hole.height,
          zIndex,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: hole.top,
          left: hole.left + hole.width,
          right: 0,
          height: hole.height,
          zIndex,
        }}
      />
    </>,
    document.body,
  );
}

/**
 * Spotlight onboarding hint: dims the whole screen via a portal cutout,
 * keeps the target lit, and shows a coachmark tip.
 *
 * Important: the target DOM ancestor chain stays stable when `open` toggles
 * (always Tooltip → Box → children) so remount-sensitive hosts like Leaflet
 * maps do not crash the page.
 */
export function SetupCoachmark({
  open,
  title,
  subtitle,
  onDismiss,
  onNext,
  nextLabel,
  fullWidth = false,
  placement: placementProp,
  targetSx,
  variant = "default",
  children,
}: SetupCoachmarkProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dim = alpha("#0f172a", 0.62);
  const isMenu = variant === "menu";
  const usePortalDim = !isMenu;
  const targetRef = useRef<HTMLDivElement>(null);
  const [hole, setHole] = useState<Hole | null>(null);

  useLayoutEffect(() => {
    if (!open || !usePortalDim) {
      setHole(null);
      return;
    }

    const measure = () => {
      const el = targetRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const pad = variant === "sidebar" ? 6 : 8;
      setHole({
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
    };

    measure();
    const raf = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const timer = window.setInterval(measure, 200);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.clearInterval(timer);
    };
  }, [open, usePortalDim, variant]);

  const tip = (
    <Box sx={{ position: "relative", textAlign: "center", pt: 0.5 }}>
      <IconButton
        aria-label={t("common.close")}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        sx={{
          position: "absolute",
          top: -10,
          insetInlineEnd: -10,
          color: "text.disabled",
          p: 0.5,
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.9375rem",
          fontWeight: 600,
          lineHeight: 1.35,
          color: "text.primary",
          mb: 0.5,
          px: 2.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.8125rem",
          fontWeight: 400,
          lineHeight: 1.45,
          color: "text.secondary",
          mb: onNext ? 1.5 : 0,
          px: 1,
        }}
      >
        {subtitle}
      </Typography>
      {onNext ? (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          sx={{ textTransform: "none", borderRadius: "10px", px: 2 }}
        >
          {nextLabel ?? t("selfSetup.tourNext")}
        </Button>
      ) : null}
    </Box>
  );

  const placement =
    placementProp ??
    (isMenu || variant === "sidebar"
      ? isMobile
        ? "top"
        : "right"
      : fullWidth || isMobile
        ? "bottom"
        : "left");

  const tooltipSlotProps = {
    popper: {
      sx: { zIndex: (t: { zIndex: { modal: number } }) => t.zIndex.modal + 8 },
      modifiers: [
        {
          name: "preventOverflow",
          options: { boundary: "viewport", padding: 16, altAxis: true },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: fullWidth
              ? placementProp === "top"
                ? ["top", "bottom"]
                : ["bottom", "top"]
              : ["right", "bottom", "top"],
          },
        },
      ],
    },
    tooltip: {
      sx: {
        maxWidth: isMobile || fullWidth ? "calc(100vw - 32px)" : 420,
        width: "max-content",
        px: 2.5,
        py: 1.75,
        bgcolor: "background.paper",
        color: "text.primary",
        borderRadius: "14px",
        border: 1,
        borderColor: "divider",
        boxShadow: "0 10px 32px rgba(15, 23, 42, 0.16)",
      },
    },
    arrow: {
      sx: {
        color: "background.paper",
        "&::before": {
          border: `1px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
        },
      },
    },
  };

  const dimZ = theme.zIndex.modal + 5;
  const isWideTarget = fullWidth || isMenu || variant === "sidebar";

  const wrapperSx = {
    display: isWideTarget ? "block" : "inline-flex",
    width: isWideTarget ? "100%" : "auto",
    maxWidth: "100%",
    borderRadius: isMenu ? 0 : "14px",
    position: "relative" as const,
    zIndex: open ? 2 : "auto",
    p: open
      ? isMenu || usePortalDim
        ? variant === "default" && !fullWidth
          ? 1.25
          : 0
        : 1.25
      : 0,
    bgcolor: open
      ? isMenu
        ? alpha(theme.palette.primary.main, 0.08)
        : "background.paper"
      : "transparent",
    outline: open
      ? isMenu
        ? `2px solid ${theme.palette.primary.main}`
        : `3px solid ${theme.palette.common.white}`
      : "none",
    outlineOffset: open ? (isMenu ? "-2px" : "2px") : 0,
    boxShadow: open
      ? isMenu
        ? "none"
        : `0 8px 28px ${alpha(theme.palette.primary.main, 0.35)}`
      : "none",
    ...targetSx,
  };

  return (
    <>
      {open && usePortalDim && hole ? (
        <ScreenDimOverlay hole={hole} dim={dim} zIndex={dimZ} />
      ) : null}
      <Tooltip
        open={open}
        arrow={open}
        placement={placement}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        disableInteractive={!open}
        title={open ? tip : ""}
        slotProps={tooltipSlotProps}
      >
        <Box ref={targetRef} sx={wrapperSx}>
          {children}
        </Box>
      </Tooltip>
    </>
  );
}
